<template>
    <div class="h-full w-full flex flex-col bg-gray-50 px-4 sm:px-0">
        <base-dialog
            :show="!!error"
            title="An error occurred!"
            @close="closeTheDialogBox"
            >
            <p>{{ error }}</p>
        </base-dialog>

        <div v-if="showForm && !upiSubmitted" class="h-full w-full flex flex-col justify-center items-center">
            <div class="w-full sm:w-2/3 md:w-3/5 lg:w-1/2 xl:w-2/5 2xl:w-1/3 p-8 bg-white rounded-lg shadow-lg border flex flex-col">
                <h2 class="text-2xl sm:text-4xl font-bold text-center">Transfer Your Fund</h2>

                <!-- Mode Selector -->
                <div class="flex flex-col mt-4">
                    <label class="mb-1">Payout Mode*</label>
                    <div class="flex gap-3 flex-wrap">
                        <div
                            v-for="mode in availablePayoutModes"
                            :key="mode"
                            @click.stop="payoutMode = mode"
                            :class="payoutMode === mode ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-600 border-red-600'"
                            class="flex-1 min-w-[70px] text-center border-2 font-medium rounded-lg px-4 py-2 text-sm cursor-pointer transition-colors"
                        >{{ mode }}</div>
                    </div>
                </div>

                <div class="flex flex-col mt-4" :class="{ invalid: !amount.isValid }">
                    <label for="amount">Enter Amount*</label>
                    <input type="text" placeholder="Amount" v-model.trim="amount.val" @blur="clearValidity('amount')" id="amount" @keypress="allowNumbersOnly">
                    <transition name="form-fade" mode="out-in">
                        <p v-if="!amount.isValid" class="h-4 text-xs text-red-600 ml-1">
                            {{ amount.msg }}
                        </p>
                    </transition>
                </div>

                <template v-if="payoutMode === 'UPI'">
                    <div class="flex flex-col mt-4" :class="{ invalid: !customerUPIID.isValid }">
                        <label for="customerUPIID">Customer UPI ID*</label>
                        <input type="text" placeholder="Customer UPI ID" v-model.trim="customerUPIID.val" @blur="clearValidity('customerUPIID')" id="customerUPIID">
                        <transition name="form-fade" mode="out-in">
                            <p v-if="!customerUPIID.isValid" class="h-4 text-xs text-red-600 ml-1">
                                {{ customerUPIID.msg }}
                            </p>
                        </transition>
                    </div>
                </template>

                <!-- Bank Transfer Modes (IMPS / NEFT / RTGS): bank account details -->
                <template v-if="bankFieldsRequired">
                    <div class="flex flex-col mt-4" :class="{ invalid: !accountNumber.isValid }">
                        <label for="accountNumber">Account Number*</label>
                        <input type="text" placeholder="Account Number" v-model.trim="accountNumber.val" @blur="clearValidity('accountNumber')" id="accountNumber">
                        <transition name="form-fade" mode="out-in">
                            <p v-if="!accountNumber.isValid" class="h-4 text-xs text-red-600 ml-1">
                                {{ accountNumber.msg }}
                            </p>
                        </transition>
                    </div>
                    <div class="flex flex-col mt-4" :class="{ invalid: !ifsc.isValid }">
                        <label for="ifsc">IFSC*</label>
                        <input type="text" placeholder="IFSC" v-model.trim="ifsc.val" @blur="clearValidity('ifsc')" id="ifsc">
                        <transition name="form-fade" mode="out-in">
                            <p v-if="!ifsc.isValid" class="h-4 text-xs text-red-600 ml-1">
                                {{ ifsc.msg }}
                            </p>
                        </transition>
                    </div>
                    <div class="flex flex-col mt-4" :class="{ invalid: !bankName.isValid }">
                        <label for="bankName">Bank Name*</label>
                        <input type="text" placeholder="bankName" v-model.trim="bankName.val" @blur="clearValidity('bankName')" id="bankName">
                        <transition name="form-fade" mode="out-in">
                            <p v-if="!bankName.isValid" class="h-4 text-xs text-red-600 ml-1">
                                {{ bankName.msg }}
                            </p>
                        </transition>
                    </div>
                </template>

                <div class="flex justify-center mt-8">
                    <div @click.stop="submitForm" class="w-32 flex justify-center items-center border-2 font-medium border-red-600 text-white hover:text-red-600 bg-red-600 hover:bg-white rounded-lg px-5 py-2 uppercase text-sm cursor-pointer">{{ getBtnText }}</div>
                </div>
            </div>
        </div>

        <div v-if="upiSubmitted" class="h-full w-full flex flex-col justify-center items-center">
            <div class="w-full sm:w-2/3 md:w-3/5 lg:w-1/2 xl:w-2/5 2xl:w-1/3 bg-white rounded-2xl shadow-lg border overflow-hidden">
                <div class="px-8 py-10 flex flex-col items-center text-center gap-4">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div class="text-2xl font-bold text-gray-800">Order Created!</div>
                    <div class="text-gray-500 text-sm">Your order has been successfully created. You will be redirected to the payment page shortly.</div>
                    <div class="flex items-center gap-2 text-sm text-gray-400 mt-1">
                        <svg class="animate-spin h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Redirecting...
                    </div>
                    <div class="text-xs text-gray-300 mt-1 uppercase tracking-wide">Receipt: {{ submittedReceiptId }}</div>
                </div>
            </div>
        </div>

        <div class="h-full fixed flex justify-center items-center" v-if="isLoading">
            <base-page-spinner-new type="spin" class-list="h-20"></base-page-spinner-new>
        </div>
    </div>
</template>

<script>
import http from "../../http-common.js";
export default {
  name: "create-payout",
  props: ["refID"],
  data() {
    return {
        error: null,
        showForm: false,
        payoutMode: "IMPS",
        upiSubmitted: false,
        utrSubmitted: false,
        submittedAmount: null,
        submittedReceiptId: "",
        amount: {
            val: null,
            isValid: true,
            msg: "",
        },
        customerUPIID: {
            val: "",
            isValid: true,
            msg: "",
        },
        accountNumber: {
            val: "",
            isValid: true,
            msg: "",
        },
        ifsc: {
            val: "",
            isValid: true,
            msg: "",
        },
        bankName: {
            val: "",
            isValid: true,
            msg: "",
        },
        utr: {
            val: "",
            isValid: true,
            msg: "",
        },
        formIsValid: true,
        isLoading: false,
    };
  },
  created() {
    this.isValidOrder();
  },
  computed: {
    getBtnText() {
        if (this.isLoading) {
            return "Loading...";
        } else {
            return "Submit";
        }
    },
    availablePayoutModes() {
        return ["IMPS", "NEFT", "RTGS", "UPI"];
    },
    bankFieldsRequired() {
        return ["IMPS", "NEFT", "RTGS"].includes(this.payoutMode);
    },
  },
  methods: {
    closeTheDialogBox() {
      this.error = null;
    },
    async isValidOrder() {
        let url = `/order-creator/isValidOrder/${this.refID}?type=payout&isPayoutLink=1`;
        try {
            const response = await http.get(url);
            if (response.status == 200 && response.data.success) {
                this.showForm = true;
            } else {
                this.error = response.data.message;
            }
        } catch (error) {
            this.error = error.response?.data?.message || "An error occurred. Please try again.";
        }
    },
    allowNumbersOnly(event) {
        const charCode = event.which || event.keyCode;
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
        }
    },
    clearValidity(input) {
        this[input].isValid = true;
        this[input].msg = "";
    },
    validateForm() {
        this.formIsValid = true;

        if (this.amount.val === null || this.amount.val === "") {
            this.amount.isValid = false;
            this.amount.msg = "Amount must not be empty.";
            this.formIsValid = false;
        }

        if (this.payoutMode === 'UPI') {
            if (this.customerUPIID.val === "") {
                this.customerUPIID.isValid = false;
                this.customerUPIID.msg = "Customer UPI ID must not be empty.";
                this.formIsValid = false;
            }
        } else {
            if (this.accountNumber.val === "") {
              this.accountNumber.isValid = false;
              this.accountNumber.msg = "Account Number must not be empty.";
              this.formIsValid = false;
            }
            if (this.ifsc.val === "") {
              this.ifsc.isValid = false;
              this.ifsc.msg = "IFSC must not be empty.";
              this.formIsValid = false;
            }
            if (this.bankName.val === "") {
              this.bankName.isValid = false;
              this.bankName.msg = "Bank Name must not be empty.";
              this.formIsValid = false;
            }
        }
    },
    async submitForm() {
        this.validateForm();

        if (!this.formIsValid) {
            return;
        }

        const formData = {
            refID: this.refID,
            amount: parseInt(this.amount.val),
            type: "payout",
            payoutMode: this.payoutMode,
        };

        if (this.bankFieldsRequired) {
            formData.accountNumber = this.accountNumber.val;
            formData.ifsc = this.ifsc.val;
            formData.bankName = this.bankName.val;
        } else if (this.payoutMode === 'UPI') {
            formData.customerUPIID = this.customerUPIID.val;
        }

        this.isLoading = true;

        try {
            const response = await http.post(`/order-creator/create-manual-order`, formData);

            if (response.status == 201 && response.data.success) {
                if (this.payoutMode === 'UPI') {
                    this.submittedAmount = parseInt(this.amount.val);
                    this.submittedReceiptId = response.data.data?.receiptId || "";
                    this.upiSubmitted = true;
                    // Redirect to payment page after short delay
                    const refID = response.data.data?.refID || this.refID;
                    const receiptId = this.submittedReceiptId || this.$route.query.receiptId;
                    setTimeout(() => {
                        this.$router.replace(`/pay/${refID}?receiptId=${receiptId}`);
                    }, 2000);
                } else {
                    // IMPS: existing swal success
                    this.$swal.fire({
                        icon: 'success',
                        title: 'Congratulations!',
                        text: "Your fund has been added successfully.",
                    }).then((result) => {
                        if (result.value || result.dismiss) {
                            this.amount.val = null;
                            this.accountNumber.val = "";
                            this.ifsc.val = "";
                            this.bankName.val = "";
                            this.$router.replace("/success");
                        }
                    });
                }
            } else {
                this.error = response.data.message;
                this.isLoading = false;
            }
        } catch (error) {
            this.error = error.response?.data?.message || "An error occurred. Please try again.";
            this.isLoading = false;
        }
    },
  },
}
</script>

<style lang="scss" scoped>
.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.5s ease;
}
.form-fade-enter-from,
.form-fade-leave-to {
  opacity: 0;
  height: 0px;
}
</style>
